const get = require('lodash/get');
const { config, getRfidEnabledRelations } = require('@sstdev/lib_metadata-config');
const schemaUtils = require('./dataDictionaryService/schemaUtils');
const routeAccessService = require('./routeAccessService');
const { addOrderConfirmationPaths } = require('./orderConfirmationApiPaths');
const { createRequestSchema } = require('./openApiWriteRequirements');
const { requiredPayloadSchema, fullPayloadSchema } = require('./hiddenWriteRequirements');
const { tagIdRequestSchema } = require('./tagIdRequestSchema');

/**
 * OpenAPI 3.1 generator.
 * Many helpers here started as copies of methods on ./swaggerService.js (Swagger 2.0)
 * and were trimmed/adjusted for OpenAPI + JSON Schema 2020-12.
 * swaggerService is legacy; new code should call buildOpenApi here instead of getSwaggerDoc there.
 */

const EXCLUDED_PATHS = ['/item/shiftSummary/aggregate'];
const EXCLUDED_NAMESPACE_RELATION_KEYS = new Set(['application.component']);
const INTERNAL_ACCESS_ROLES = Object.freeze(['USER', 'ADMIN', 'SUPPORT', 'DEVICE']);
const PUBLIC_OPENAPI_ROLES = Object.freeze(['USER', 'ADMIN']);
const ACCESS_METHODS = Object.freeze(['get', 'post', 'patch', 'remove']);
const OPERATION_SUMMARIES = Object.freeze({
    create: 'create',
    list: 'list',
    getById: 'get-by-id',
    aggregate: 'aggregate',
    patchById: 'patch-by-id',
    deleteById: 'delete-by-id'
});
const RESPONSE_DESCRIPTIONS = Object.freeze({
    created: 'created',
    success: 'success',
    noContent: 'no-content',
    noResults: 'no-results',
    notModified: 'not-modified',
    authRequired: 'auth-required',
    conflict: 'conflict',
    resourceInUse: 'resource-in-use',
    invalidRequest: 'invalid-request',
    invalidPatch: 'invalid-patch'
});

function namespaceRelationKey(namespaceTitle, relationTitle) {
    return `${namespaceTitle}.${relationTitle}`;
}

function isExcludedNamespaceRelation(namespaceTitle, relationTitle) {
    return EXCLUDED_NAMESPACE_RELATION_KEYS.has(namespaceRelationKey(namespaceTitle, relationTitle));
}

function omitExcludedComponentSchemas(schemas = {}) {
    return Object.entries(schemas).reduce((acc, [schemaName, schema]) => {
        if (EXCLUDED_NAMESPACE_RELATION_KEYS.has(schemaName)) return acc;
        acc[schemaName] = schema;
        return acc;
    }, {});
}

// mirrors swaggerService.prettyNamespaceRelation but uses metadata namespace/relation shape
function prettyNamespaceRelation(namespace, relation, plural = false) {
    const ns = namespace.prettyName || namespace.title;
    const rel = relation.prettyName || relation.title;
    const pluralization = plural && rel[rel.length - 1] !== 's' ? 's' : '';
    return ns === rel || rel.startsWith(ns) ? rel + pluralization : `${ns} ${rel + pluralization}`;
}

/**
 * Recursively rewrites $ref paths from one base to another.
 * Creates new objects rather than mutating, so no cloneDeep needed.
 * @param {*} obj - The object to transform
 * @param {string} from - The prefix to replace (e.g., '#/$defs/')
 * @param {string} to - The replacement prefix (e.g., '#/components/schemas/')
 * @returns {*} - Transformed object with rewritten refs
 */
function rewriteRefs(obj, from, to) {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(item => rewriteRefs(item, from, to));

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        if (key === '$ref' && typeof value === 'string' && value.startsWith(from)) {
            result[key] = to + value.slice(from.length);
        } else if (key !== '$defs') {
            result[key] = rewriteRefs(value, from, to);
        }
        // Drop nested $defs silently
    }
    return result;
}

/**
 * Recursively strips x-* extension properties from a schema.
 * Used for cleaner documentation output.
 * @param {*} obj - The object to transform
 * @returns {*} - Object with x-* properties removed
 */
function stripExtensions(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(stripExtensions);

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        if (!key.startsWith('x-')) {
            result[key] = stripExtensions(value);
        }
    }
    return result;
}

/**
 * Converts JSON Schema $defs to OpenAPI components/schemas format.
 * Rewrites $ref paths and optionally strips x-* extensions.
 * @param {object} rootSchema - The root JSON Schema with $defs
 * @param {{forDocs?: boolean}} options - Options for transformation
 * @returns {{schemas: object}} - OpenAPI-compatible schemas object
 */
function hoistDefs(rootSchema, { forDocs = false } = {}) {
    const defs = rootSchema.$defs || {};
    const schemas = {};

    for (const [key, schema] of Object.entries(defs)) {
        let converted = rewriteRefs(schema, '#/$defs/', '#/components/schemas/');
        if (forDocs) {
            converted = stripExtensions(converted);
        }
        schemas[key] = converted;
    }

    return { schemas };
}

function buildAggregateSchema() {
    return {
        type: 'object',
        description:
            'The result of an aggregate operation. Example: [{ "_id": { "title": "Total" }, "count": 16 }]',
        required: ['_id', 'count'],
        properties: {
            _id: {
                type: 'object',
                properties: {
                    title: { type: 'string', description: 'The value of the groupBy property, or "Total" if none.' }
                }
            },
            count: { type: 'integer' }
        }
    };
}

function buildJsonPatchSchema() {
    return {
        type: 'object',
        required: ['op', 'path'],
        properties: {
            op: { type: 'string', enum: ['test', 'add', 'remove', 'replace'] },
            path: { type: 'string' },
            value: {}
        }
    };
}

// derived from SwaggerService#createTags; works on metadata.namespaces instead of raw dataDictionary
function createTags(metadata, forDocs = false) {
    const tags = [];
    const namespaces = metadata?.namespaces || [];
    namespaces
        .filter(ns => !forDocs || (!ns.hideApiDocs && !ns.withCustomApi))
        .forEach(ns => {
            // Only create tag if namespace contains any non-exception relation.
            // In docs mode, also require at least one visible relation (hideApiDocs=false).
            const hasNonException = (ns.relations || []).some(rel => {
                if (isExcludedNamespaceRelation(ns.title, rel.title)) return false;
                const ex = config.allowedNamespaceRelationExceptions[ns.title];
                if (ex && ex[rel.title]) return false;
                if (forDocs && rel.hideApiDocs) return false;
                return true;
            });
            if (!hasNonException) return;
            // Fallback description if there is no description in the metadata
            tags.push({
                name: ns.prettyName || ns.title,
                description: ns.description || `Operations for ${ns.prettyName || ns.title}`
            });
        });
    return tags;
}

function buildPathNamespaces(metadata, accessMap, hasRfidRelations) {
    const namespaces = new Map();

    (metadata?.namespaces || []).forEach(namespace => {
        const relations = [...(namespace.relations || [])].filter(
            relation => hasRfidRelations || namespace.title !== 'application' || relation.title !== 'rfidEnabledRelation'
        );
        namespaces.set(namespace.title, {
            ...namespace,
            relations
        });
    });

    Object.entries(accessMap || {}).forEach(([namespaceTitle, relations]) => {
        const namespace =
            namespaces.get(namespaceTitle) ||
            {
                title: namespaceTitle,
                prettyName: namespaceTitle,
                hideApiDocs: false,
                withCustomApi: false,
                relations: []
            };

        Object.keys(relations || {}).forEach(relationTitle => {
            if (!hasRfidRelations && namespaceTitle === 'application' && relationTitle === 'rfidEnabledRelation') return;
            if (namespace.relations.some(relation => relation.title === relationTitle)) return;
            namespace.relations.push({
                title: relationTitle,
                prettyName: relationTitle,
                hideApiDocs: false,
                withCustomApi: false
            });
        });

        namespaces.set(namespaceTitle, namespace);
    });

    if (hasRfidRelations) {
        const application = namespaces.get('application') || {
            title: 'application', prettyName: 'Application', relations: []
        };
        if (!application.relations.some(relation => relation.title === 'rfidEnabledRelation')) {
            application.relations.push({ title: 'rfidEnabledRelation', prettyName: 'RFID Enabled Relation' });
        }
        namespaces.set('application', application);
    }

    return Array.from(namespaces.values());
}

/**
 * Normalize route access payload into { [namespace]: { [relation]: methodAccess } }.
 * Accepts either routeAccessService output ({ namespaces: [...] }) or an already-normalized map.
 * @param {object} access
 * @returns {Record<string, Record<string, {get?:boolean,post?:boolean,patch?:boolean,remove?:boolean}>>}
 */
function accessToMap(access) {
    if (access && Array.isArray(access.namespaces)) {
        const map = {};
        access.namespaces.forEach(ns => {
            const nsTitle = ns.title;
            map[nsTitle] = map[nsTitle] || {};
            (ns.relations || []).forEach(rel => {
                map[nsTitle][rel.title] = rel.methodAccess || {};
            });
        });
        return map;
    }
    return access || {};
}

/**
 * Build a stable operation key used for internal x-access-control annotations.
 * @param {string} namespaceTitle
 * @param {string} relationTitle
 * @param {'get'|'post'|'patch'|'remove'} method
 * @returns {string}
 */
function getOperationAccessKey(namespaceTitle, relationTitle, method) {
    return `${namespaceTitle}.${relationTitle}.${method}`;
}

/**
 * Attach internal access-control metadata to an operation.
 * @param {object} operation
 * @param {string} operationAccessKey
 * @param {Record<string, string[]>} [allowedRolesByOperation]
 * @param {boolean} [includeAccessControl]
 * @returns {object}
 */
function withAccessControl(operation, operationAccessKey, allowedRolesByOperation = {}, includeAccessControl = false) {
    if (!includeAccessControl) return operation;

    return {
        ...operation,
        'x-access-control': {
            allowedRoles: [...(allowedRolesByOperation[operationAccessKey] || [])]
        }
    };
}

/**
 * Mark operations that are not available to USER or ADMIN as internal/private.
 * @param {string} operationAccessKey
 * @param {Record<string, string[]>} [allowedRolesByOperation]
 * @param {boolean} [includeAccessControl]
 * @returns {boolean}
 */
function isPrivateOperation(operationAccessKey, allowedRolesByOperation = {}, includeAccessControl = false) {
    if (!includeAccessControl) return false;
    const allowedRoles = allowedRolesByOperation[operationAccessKey];
    if (!Array.isArray(allowedRoles)) return false;
    return PUBLIC_OPENAPI_ROLES.every(role => !allowedRoles.includes(role));
}

/**
 * Attach operation-level OpenAPI extensions for internal docs.
 * @param {object} operation
 * @param {string} operationAccessKey
 * @param {Record<string, string[]>} [allowedRolesByOperation]
 * @param {boolean} [includeAccessControl]
 * @param {boolean} [forcePrivate]
 * @returns {object}
 */
function withOperationExtensions(
    operation,
    operationAccessKey,
    allowedRolesByOperation = {},
    includeAccessControl = false,
    forcePrivate = false
) {
    const operationWithAccessControl = withAccessControl(
        operation,
        operationAccessKey,
        allowedRolesByOperation,
        includeAccessControl
    );

    if (!includeAccessControl) {
        return operationWithAccessControl;
    }

    const privateOperation =
        forcePrivate || isPrivateOperation(operationAccessKey, allowedRolesByOperation, includeAccessControl);
    if (!privateOperation) {
        return operationWithAccessControl;
    }

    return {
        ...operationWithAccessControl,
        'x-private': true
    };
}

/**
 * Compute per-operation role visibility and a union access map across internal-tracked roles.
 *
 * Important: route access depends on role-aware hNode visibility. Do not reuse a schema
 * that was built for one role when calculating access for another role.
 *
 * @param {{ metadata: object, featureFlags?: string[] }} options
 * @returns {{ unionAccessMap: object, allowedRolesByOperation: Record<string, string[]> }}
 */
function computeAllRoleAccess({ metadata, featureFlags }) {
    const unionAccessMap = {};
    const allowedRolesByOperation = {};

    INTERNAL_ACCESS_ROLES.forEach(role => {
        // Let routeAccessService build role-specific schema context internally.
        const roleAccess = routeAccessService.getRouteAccess(metadata, role, undefined, { featureFlags });
        const roleAccessMap = accessToMap(roleAccess);

        Object.entries(roleAccessMap).forEach(([namespaceTitle, relations]) => {
            unionAccessMap[namespaceTitle] = unionAccessMap[namespaceTitle] || {};

            Object.entries(relations || {}).forEach(([relationTitle, methodAccess]) => {
                unionAccessMap[namespaceTitle][relationTitle] = unionAccessMap[namespaceTitle][relationTitle] || {
                    get: false,
                    post: false,
                    patch: false,
                    remove: false
                };

                ACCESS_METHODS.forEach(method => {
                    if (!methodAccess?.[method]) return;

                    unionAccessMap[namespaceTitle][relationTitle][method] = true;
                    const operationAccessKey = getOperationAccessKey(namespaceTitle, relationTitle, method);

                    if (!allowedRolesByOperation[operationAccessKey]) {
                        allowedRolesByOperation[operationAccessKey] = [];
                    }
                    allowedRolesByOperation[operationAccessKey].push(role);
                });
            });
        });
    });

    return {
        unionAccessMap,
        allowedRolesByOperation
    };
}

// parameters roughly match SwaggerService list query params, but expressed as OpenAPI 3 parameter objects
function buildListQueryParameters({ applyPagingDefaults = true } = {}) {
    const pagingSchema = value => ({ type: 'integer', ...(applyPagingDefaults ? { default: value } : {}) });
    return [
        { name: 'correlationId', in: 'query', required: false, schema: { type: 'string' } },
        { name: 'offset', in: 'query', required: false, schema: pagingSchema(0), description: 'Offset-based pagination. Use with limit. Mutually exclusive with page and pageSize.' },
        { name: 'limit', in: 'query', required: false, schema: pagingSchema(100), description: 'Limit-based pagination. Use with offset. Mutually exclusive with page and pageSize.' },
        { name: 'page', in: 'query', required: false, schema: pagingSchema(1), description: 'Page-based pagination. Use with pageSize. Mutually exclusive with offset and limit.' },
        { name: 'pageSize', in: 'query', required: false, schema: pagingSchema(25), description: 'Page size for page-based pagination. Use with page. Mutually exclusive with offset and limit.' },
        {
            name: 'orderBy',
            in: 'query',
            required: false,
            schema: { type: 'string', ...(applyPagingDefaults ? { default: 'title:a' } : {}) },
            description:
                'Property to sort by; add suffix ":a" for ascending or ":d" for descending (e.g., title:a).'
        },
        { name: 'searchTerm', in: 'query', required: false, schema: { type: 'string' } },
        { name: 'searchTermLowerBound', in: 'query', required: false, schema: { type: 'string', format: 'date-time' } },
        { name: 'searchTermUpperBound', in: 'query', required: false, schema: { type: 'string', format: 'date-time' } },
        { name: 'exact', in: 'query', required: false, schema: { type: 'boolean' } },
        { name: 'excludeProperty', in: 'query', required: false, schema: { type: 'string' } },
        {
            name: 'excludePropertyValues',
            in: 'query',
            required: false,
            schema: { type: 'array', items: { type: 'string' } }
        },
        { name: 'includeDeleted', in: 'query', required: false, schema: { type: 'boolean' } },
        {
            name: 'searchProperties',
            in: 'query',
            required: false,
            schema: { type: 'array', items: { type: 'string' } }
        },
        { name: 'criteria', in: 'query', required: false, schema: { type: 'string' } }
    ];
}

/**
 * Build OpenAPI paths from metadata, route access, and component schemas.
 * High-level flow is adapted from SwaggerService#createPaths + per-method helpers.
 */
function createPaths({
    metadata,
    tenantSettings = {},
    access,
    componentsSchemas,
    rawSchemas,
    forDocs = false,
    forValidation = false,
    role,
    includeAccessControl = false,
    allowedRolesByOperation = {},
    hasRfidRelations = false
}) {
    const paths = {};
    const defsHas = name => !!componentsSchemas[name];
    const isPrivilegedRole = ['SUPPORT', 'DEITY'].includes(role);
    const accessMap = accessToMap(access);
    if (hasRfidRelations) {
        accessMap.application = accessMap.application || {};
        accessMap.application.rfidEnabledRelation = { get: true };
    }
    const namespaces = buildPathNamespaces(metadata, accessMap, hasRfidRelations);

    namespaces.forEach(ns => {
        // In docs mode, omit hidden namespaces and namespaces served by a custom API.
        if (forDocs && (ns.hideApiDocs || ns.withCustomApi)) return;

        const nsTitle = ns.title;
        const tagName = ns.prettyName || ns.title;
        const relations = ns.relations || [];
        relations.forEach(rel => {
            const relTitle = rel.title;
            if (isExcludedNamespaceRelation(nsTitle, relTitle)) return;
            const nsEx = config.allowedNamespaceRelationExceptions[nsTitle];
            if (nsEx && nsEx[relTitle]) return;
            const relationHiddenFromPublicDocs = !!(ns.hideApiDocs || ns.withCustomApi || rel.hideApiDocs);

            // In docs mode, omit relations explicitly hidden from API docs.
            if (forDocs && rel.hideApiDocs) return;

            const relAccess = get(accessMap, [nsTitle, relTitle], {});
            const basePath = `/${nsTitle}/${relTitle}`;
            const idPath = `${basePath}/{id}`;
            const aggregatePath = `${basePath}/aggregate`;
            const schemaName = `${nsTitle}.${relTitle}`;
            const ref = `#/components/schemas/${schemaName}`;
            // If generating docs for end-user roles, require that a schema exists for this relation
            if (forDocs && !isPrivilegedRole && !defsHas(schemaName)) return;

            if (EXCLUDED_PATHS.includes(aggregatePath)) {
                // still allow base and id paths
            }

            if (relAccess.post) {
                const createSchema = tagIdRequestSchema(
                    createRequestSchema(schemaName, componentsSchemas, rawSchemas),
                    componentsSchemas,
                    { namespace: nsTitle, relation: relTitle, tenantSettings, forValidation }
                );
                // Custom APIs, virtual transactions and application reset accept fields beyond their UI forms.
                // Reset accepts per-relation filter objects where the UI only displays checkboxes.
                // Documentation visibility alone does not identify such a contract.
                const handlerOwnsPayload = ns.withCustomApi || nsTitle === 'transaction' || relTitle.endsWith('-transaction') ||
                    (nsTitle === 'application' && relTitle === 'reset');
                const payloadSchema = forValidation && relationHiddenFromPublicDocs
                    ? (handlerOwnsPayload ? requiredPayloadSchema : fullPayloadSchema)(createSchema, componentsSchemas)
                    : createSchema;
                paths[basePath] = paths[basePath] || {};
                paths[basePath].post = withOperationExtensions(
                    {
                        tags: [tagName],
                        summary: OPERATION_SUMMARIES.create,
                        description: `Insert your new ${prettyNamespaceRelation(ns, rel)}.`,
                        operationId: `post_${nsTitle}_${relTitle}`,
                        requestBody: {
                            required: true,
                            content: { 'application/json': { schema: payloadSchema } }
                        },
                        responses: {
                            201: {
                                description: RESPONSE_DESCRIPTIONS.created,
                                content: {
                                    'application/json': {
                                        schema: defsHas(schemaName) ? { $ref: ref } : { type: 'object' }
                                    }
                                }
                            },
                            400: { description: RESPONSE_DESCRIPTIONS.invalidRequest },
                            // A create can conflict with existing data -- a duplicate on a unique
                            // index, or a referenced record that is not in a usable state -- which
                            // has always come back as a 409 but was never documented.
                            409: { description: RESPONSE_DESCRIPTIONS.conflict },
                            403: { description: RESPONSE_DESCRIPTIONS.authRequired }
                        }
                    },
                    getOperationAccessKey(nsTitle, relTitle, 'post'),
                    allowedRolesByOperation,
                    includeAccessControl,
                    relationHiddenFromPublicDocs
                );
            }

            if (relAccess.get) {
                paths[basePath] = paths[basePath] || {};
                paths[basePath].get = withOperationExtensions(
                    {
                        tags: [tagName],
                        summary: OPERATION_SUMMARIES.list,
                        description: `Get a page of ${prettyNamespaceRelation(ns, rel, true)}.`,
                        operationId: `get_${nsTitle}_${relTitle}`,
                        parameters: buildListQueryParameters({
                            applyPagingDefaults: !(forValidation && relationHiddenFromPublicDocs)
                        }),
                        responses: {
                            200: {
                                description: RESPONSE_DESCRIPTIONS.success,
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                items: {
                                                    type: 'array',
                                                    items: buildRecordSchema(schemaName, ref, defsHas)
                                                }
                                            }
                                        }
                                    }
                                }
                            },
                            204: { description: RESPONSE_DESCRIPTIONS.noContent },
                            304: { description: RESPONSE_DESCRIPTIONS.notModified },
                            403: { description: RESPONSE_DESCRIPTIONS.authRequired }
                        }
                    },
                    getOperationAccessKey(nsTitle, relTitle, 'get'),
                    allowedRolesByOperation,
                    includeAccessControl,
                    relationHiddenFromPublicDocs
                );

                paths[idPath] = paths[idPath] || {};
                paths[idPath].get = withOperationExtensions(
                    {
                        tags: [tagName],
                        summary: OPERATION_SUMMARIES.getById,
                        description: `Get a single ${prettyNamespaceRelation(ns, rel)} using its Id.`,
                        operationId: `get_${nsTitle}_${relTitle}_byId`,
                        parameters: [
                            { name: 'id', in: 'path', required: true, schema: { type: 'string' }, description: `The id of the ${prettyNamespaceRelation(ns, rel)}` }
                        ],
                        responses: {
                            200: {
                                description: RESPONSE_DESCRIPTIONS.success,
                                content: {
                                    'application/json': {
                                        schema: buildItemsResponseSchema(schemaName, ref, defsHas)
                                    }
                                }
                            },
                            204: { description: RESPONSE_DESCRIPTIONS.noResults },
                            403: { description: RESPONSE_DESCRIPTIONS.authRequired }
                        }
                    },
                    getOperationAccessKey(nsTitle, relTitle, 'get'),
                    allowedRolesByOperation,
                    includeAccessControl,
                    relationHiddenFromPublicDocs
                );

                if (!EXCLUDED_PATHS.includes(aggregatePath)) {
                    paths[aggregatePath] = paths[aggregatePath] || {};
                    paths[aggregatePath].get = withOperationExtensions(
                        {
                            tags: [tagName],
                            summary: OPERATION_SUMMARIES.aggregate,
                            description: `Get counts over ${prettyNamespaceRelation(ns, rel, true)}.`,
                            operationId: `get_${nsTitle}_${relTitle}_aggregate`,
                            parameters: [
                                { name: 'groupBy', in: 'query', required: false, schema: { type: 'string' } },
                                { name: 'searchTerm', in: 'query', required: false, schema: { type: 'string' } },
                                { name: 'exact', in: 'query', required: false, schema: { type: 'boolean' } },
                                { name: 'searchTermLowerBound', in: 'query', required: false, schema: { type: 'string', format: 'date-time' } },
                                { name: 'searchTermUpperBound', in: 'query', required: false, schema: { type: 'string', format: 'date-time' } },
                                { name: 'searchProperties', in: 'query', required: false, schema: { type: 'array', items: { type: 'string' } } },
                                { name: 'includeDeleted', in: 'query', required: false, schema: { type: 'boolean' } }
                            ],
                            responses: {
                                200: {
                                    description: RESPONSE_DESCRIPTIONS.success,
                                    content: { 'application/json': { schema: { type: 'array', items: { $ref: '#/components/schemas/Aggregate' } } } }
                                }
                            }
                        },
                        getOperationAccessKey(nsTitle, relTitle, 'get'),
                        allowedRolesByOperation,
                        includeAccessControl,
                        relationHiddenFromPublicDocs
                    );
                }
            }

            if (relAccess.patch) {
                paths[idPath] = paths[idPath] || {};
                paths[idPath].patch = withOperationExtensions(
                    {
                        tags: [tagName],
                        summary: OPERATION_SUMMARIES.patchById,
                        description: `Patch this ${prettyNamespaceRelation(ns, rel)}.`,
                        operationId: `patch_${nsTitle}_${relTitle}_byId`,
                        parameters: [
                            { name: 'id', in: 'path', required: true, schema: { type: 'string' }, description: `The id of the ${prettyNamespaceRelation(ns, rel)}` }
                        ],
                        requestBody: {
                            required: true,
                            content: {
                                'application/json-patch+json': {
                                    schema: { type: 'array', items: { $ref: '#/components/schemas/JsonPatch' } }
                                }
                            }
                        },
                        responses: {
                            200: {
                                description: RESPONSE_DESCRIPTIONS.success,
                                content: {
                                    'application/json': {
                                        schema: defsHas(schemaName) ? { $ref: ref } : { type: 'object' }
                                    }
                                }
                            },
                            400: { description: RESPONSE_DESCRIPTIONS.invalidPatch },
                            409: { description: RESPONSE_DESCRIPTIONS.conflict },
                            403: { description: RESPONSE_DESCRIPTIONS.authRequired }
                        }
                    },
                    getOperationAccessKey(nsTitle, relTitle, 'patch'),
                    allowedRolesByOperation,
                    includeAccessControl,
                    relationHiddenFromPublicDocs
                );
            }

            if (relAccess.remove) {
                paths[idPath] = paths[idPath] || {};
                paths[idPath].delete = withOperationExtensions(
                    {
                        tags: [tagName],
                        summary: OPERATION_SUMMARIES.deleteById,
                        description: `Remove a single ${prettyNamespaceRelation(ns, rel)} using its Id.`,
                        operationId: `delete_${nsTitle}_${relTitle}_byId`,
                        parameters: [
                            { name: 'id', in: 'path', required: true, schema: { type: 'string' }, description: `The id of the ${prettyNamespaceRelation(ns, rel)}` }
                        ],
                        responses: {
                            200: {
                                description: RESPONSE_DESCRIPTIONS.success,
                                content: {
                                    'application/json': {
                                        schema: defsHas(schemaName) ? { $ref: ref } : { type: 'object' }
                                    }
                                }
                            },
                            400: {
                                description: RESPONSE_DESCRIPTIONS.resourceInUse
                            },
                            403: { description: RESPONSE_DESCRIPTIONS.authRequired }
                        },
                    },
                    getOperationAccessKey(nsTitle, relTitle, 'remove'),
                    allowedRolesByOperation,
                    includeAccessControl,
                    relationHiddenFromPublicDocs
                );
            }
        });
    });

    // manual filter for special paths
    const filtered = Object.entries(paths).reduce((acc, [key, value]) => {
        if (EXCLUDED_PATHS.includes(key)) return acc;
        acc[key] = value;
        return acc;
    }, {});
    return filtered;
}

function buildItemsResponseSchema(schemaName, ref, defsHas) {
    return {
        type: 'object',
        properties: {
            items: {
                type: 'array',
                items: buildRecordSchema(schemaName, ref, defsHas)
            }
        }
    };
}

function buildRecordSchema(schemaName, ref, defsHas) {
    return defsHas(schemaName) ? { $ref: ref } : { type: 'object' };
}

function getSchemaNameFromComponentsRef(ref) {
    if (typeof ref !== 'string') return undefined;
    const prefix = '#/components/schemas/';
    if (!ref.startsWith(prefix)) return undefined;
    // Defensive: in case a ref points into a subschema, keep only the component name
    const remainder = ref.slice(prefix.length);
    return remainder.split('/')[0];
}

function collectComponentSchemaRefs(obj, enqueue) {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
        obj.forEach(item => collectComponentSchemaRefs(item, enqueue));
        return;
    }

    Object.entries(obj).forEach(([key, value]) => {
        if (key === '$ref') {
            const schemaName = getSchemaNameFromComponentsRef(value);
            if (schemaName) enqueue(schemaName);
            return;
        }
        collectComponentSchemaRefs(value, enqueue);
    });
}

/**
 * Prune components.schemas to only those schemas reachable from paths via $ref.
 * This keeps docs output cleaner while preserving $ref closure so the result is valid.
 * @param {object} paths - OpenAPI paths object
 * @param {object} componentsSchemas - OpenAPI components.schemas object
 * @returns {object} pruned components.schemas
 */
function pruneComponentsSchemasForDocs(paths, componentsSchemas) {
    const kept = new Set();
    const queue = [];
    const enqueue = name => {
        if (!name || kept.has(name)) return;
        kept.add(name);
        queue.push(name);
    };

    collectComponentSchemaRefs(paths, enqueue);

    while (queue.length > 0) {
        const name = queue.pop();
        const schema = componentsSchemas?.[name];
        if (!schema) continue;
        collectComponentSchemaRefs(schema, enqueue);
    }

    return Array.from(kept).reduce((acc, name) => {
        if (componentsSchemas?.[name]) acc[name] = componentsSchemas[name];
        return acc;
    }, {});
}

/**
 * Build the OpenAPI 3.1 document for the given metadata/use case.
 * This is the OpenAPI replacement for SwaggerService#getSwaggerDoc.
 * Swagger will eventually be removed.
 */
function buildOpenApi({
    metadata,
    role,
    useCaseId,
    tenantSettings = {},
    tenantId,
    featureFlags,
    forDocs = false,
    forValidation = false,
    includeAccessControl = false
}) {
    if (!metadata) throw new Error('metadata is required');
    const nodeTree = metadata.hNodes;
    if (!nodeTree) throw new Error('hNode is required');
    const buildContext = {
        'identity:role': { title: role || 'ADMIN' },
        allFeatureFlags: Array.isArray(featureFlags)
            ? featureFlags
            : Array.isArray(metadata?.featureFlags)
              ? metadata.featureFlags
              : []
    };
    const rootSchema = schemaUtils.buildSchema(nodeTree, {
        id: `urn:openapi:${useCaseId || 'usecase'}`,
        title: metadata.title,
        context: buildContext
    });
    const { schemas: componentsSchemas } = hoistDefs(rootSchema, { forDocs });
    const hasRfidRelations =
        getRfidEnabledRelations(metadata.namespaces || [], { throwOnInvalid: false }).length > 0;
    if (hasRfidRelations) {
        componentsSchemas['application.rfidEnabledRelation'] = {
            type: 'object',
            properties: { _id: { type: 'string' }, title: { type: 'string' } },
            required: ['_id', 'title'],
            additionalProperties: false
        };
    } else {
        delete componentsSchemas['application.rfidEnabledRelation'];
    }
    // Pass the pre-built schema to avoid duplicate schema building
    let access = routeAccessService.getRouteAccess(metadata, role, rootSchema, {
        featureFlags: buildContext.allFeatureFlags,
        includeVirtualRfidRelation: hasRfidRelations
    });
    let allowedRolesByOperation = {};

    if (includeAccessControl) {
        const allRoleAccess = computeAllRoleAccess({
            metadata,
            featureFlags: buildContext.allFeatureFlags
        });
        access = allRoleAccess.unionAccessMap;
        allowedRolesByOperation = allRoleAccess.allowedRolesByOperation;
        if (hasRfidRelations) {
            allowedRolesByOperation['application.rfidEnabledRelation.get'] = [...PUBLIC_OPENAPI_ROLES, 'SUPPORT', 'DEVICE', 'DEITY'];
        }
    }

    const tags = createTags(metadata, forDocs);
    const paths = createPaths({
        metadata,
        tenantSettings,
        access,
        componentsSchemas,
        rawSchemas: rootSchema.$defs,
        forDocs,
        forValidation,
        role,
        includeAccessControl,
        allowedRolesByOperation,
        hasRfidRelations
    });
    // The Order Confirmation action endpoints are not in the metadata, so they are added for a tenant
    // that has the feature — see orderConfirmationApiPaths.js.
    addOrderConfirmationPaths(paths, { tenantSettings });

    const prunedSchemas = forDocs ? pruneComponentsSchemasForDocs(paths, componentsSchemas) : componentsSchemas;
    const filteredSchemas = omitExcludedComponentSchemas(prunedSchemas);
    const components = {
        schemas: Object.assign({}, filteredSchemas, { Aggregate: buildAggregateSchema(), JsonPatch: buildJsonPatchSchema() })
    };

    return {
        openapi: '3.1.0',
        jsonSchemaDialect: 'https://spec.openapis.org/oas/3.1/dialect/base',
        info: {
            title: metadata.title,
            version: get(metadata, 'release', '1.0.0')
        },
        servers: [{ url: '/api' }],
        tags,
        paths,
        components,
        'x-usecase-id': useCaseId || '',
        'x-tenant-settings': tenantSettings || {},
        'x-tenant-id': tenantId || '',
        'x-user-role': role || ''
    };
}

module.exports = {
    buildOpenApi,
    _private: {
        pruneComponentsSchemasForDocs,
        computeAllRoleAccess,
        accessToMap
    }
};
